import {
  useState,
} from 'react';
import {
  Callout,
  Facts,
  Modal,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  formatDate,
  useLoad,
  countryLabel,
  rowOpen,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  affiliateStatusTone,
  affiliatesOnBrand,
} from '../../screens.ts';
import { Failure, OperatorFigures, STANDARD_BRAND_HEX } from '../shared.tsx';
import { AffiliateDetailModal } from '../affiliates/AffiliateDetailModal.tsx';
import { BrandSettings, ProposeBrandChange } from './BrandSettings.tsx';
import { BrandActions } from './BrandActions.tsx';
import { useAbilities } from '../abilities.tsx';
import { brandActions, brandStanding } from '../entity-actions.ts';
import { t } from '../../messages.ts';

export interface BrandDetailBrand {
  brand_id: string; code: string; display_name: string; base_currency: string;
  tracking_domain: string; status: string; products: string[];
  theme_accent?: string | null;
  default_url?: string | null;
  website_url?: string | null;
  logo_url?: string | null;
  /** The operator's name, when opened from the cross-operator list. */
  operator?: string;
  /** The operator's lifecycle state: a deleted operator offers no login-as. */
  operator_state?: string;
  soft_deleted_at?: string | null;
  purged_at?: string | null;
}

export function BrandDetailModal({
  api,
  operatorId,
  brand,
  onChanged,
  onClose,
}: {
  api: PlatformApi;
  operatorId: string;
  brand: BrandDetailBrand;
  /**
   * Told when the brand's colour or its standing (deleted, restored, purged)
   * changed, so the list behind this re-reads. After a standing change the
   * popup closes itself: what it showed is no longer what is there.
   */
  onChanged?: () => void;
  onClose: () => void;
}) {
  // A colour just saved, shown before the list behind has re-read.
  const [colour, setColour] = useState<string | null | undefined>(undefined);
  // Likewise a logo just saved or removed: undefined is "as listed".
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const logoUrl = logo === undefined ? (brand.logo_url ?? null) : logo;
  const theme = colour === undefined ? (brand.theme_accent ?? null) : colour;
  // 3.9d — a status or profile just saved here, shown before the list behind
  // has re-read (the popup holds its own copy of the row).
  const [status, setStatus] = useState(brand.status);
  const [profile, setProfile] = useState({
    display_name: brand.display_name,
    base_currency: brand.base_currency,
    default_url: brand.default_url ?? null,
  });
  const shown: BrandDetailBrand = { ...brand, ...profile, status };
  const standing = brandStanding(brand);
  const gone = standing.deleted || standing.purged;

  return (
    <Modal wide title={shown.display_name} onClose={onClose}>
      <BrandActions
        api={api}
        operatorId={operatorId}
        brand={{ ...shown, theme_accent: theme, logo_url: logoUrl }}
        onColour={(c) => { setColour(c); onChanged?.(); }}
        onLogo={(l) => { setLogo(l); onChanged?.(); }}
        onStatus={(st) => { setStatus(st); onChanged?.(); }}
        onLifecycle={() => { onChanged?.(); onClose(); }}
      />

      <Facts
        items={[
          ...(brand.operator ? [{ label: t('common.operator'), value: brand.operator }] : []),
          { label: t('brand.detail.code'), value: brand.code },
          { label: t('brand.detail.currency'), value: shown.base_currency },
          { label: t('brand.detail.domain'), value: brand.tracking_domain },
          ...(brand.website_url ? [{ label: t('ent.brand.websiteUrl'), value: brand.website_url }] : []),
          ...(shown.default_url ? [{ label: t('ent.brand.defaultUrl'), value: shown.default_url }] : []),
          {
            label: t('ent.brand.colour'),
            value: (
              <span className="row" style={{ flexWrap: 'nowrap' }}>
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-block', flex: 'none', width: '14px', height: '14px',
                    borderRadius: '50%', border: '1px solid var(--line-strong)',
                    background: theme ?? STANDARD_BRAND_HEX,
                  }}
                />
                <span className="mono">{theme ?? t('brand.panel.standard')}</span>
              </span>
            ),
          },
          {
            label: t('brand.detail.products'),
            value: brand.products.length === 0 ? '—' : brand.products.join(', '),
          },
          ...(brand.soft_deleted_at
            ? [{ label: t('ent.state.deletedOn'), value: formatDate(brand.soft_deleted_at) }]
            : []),
          ...(brand.purged_at
            ? [{ label: t('ent.state.purgedOn'), value: formatDate(brand.purged_at) }]
            : []),
        ]}
      />

      {gone ? (
        <Callout>{standing.purged ? t('ent.brand.purgedNote') : t('ent.brand.deletedNote')}</Callout>
      ) : (
        <BrandLiveSections
          api={api}
          operatorId={operatorId}
          brand={shown}
          onProfile={(p) => { setProfile(p); onChanged?.(); }}
        />
      )}
    </Modal>
  );
}

/**
 * What a live brand has beyond its own row: its figures, the affiliates
 * enrolled on it, a form to propose a change and its settings. A deleted or
 * purged brand has none of these to show or act on, so none is read for it.
 */
function BrandLiveSections({
  api,
  operatorId,
  brand,
  onProfile,
}: {
  api: PlatformApi;
  operatorId: string;
  brand: BrandDetailBrand;
  /** The profile was saved directly (3.9d). */
  onProfile: (p: { display_name: string; base_currency: string; default_url: string | null }) => void;
}) {
  const { can } = useAbilities();
  const mayPropose = brandActions({ deleted: false, purged: false }, can).includes('propose-change');
  /** Which enrolled affiliate's own popup is open, opened from this brand. */
  const [openAffiliate, setOpenAffiliate] = useState<string | null>(null);
  /** The confirmation from a proposal, kept where the form that made it is. */
  const [proposed, setProposed] = useState<string | null>(null);
  // Whether that popup changed something this list shows — an enrollment
  // suspended here drops the row, a status change moves its chip.
  const [affiliateChanged, setAffiliateChanged] = useState(false);
  const [affiliatesVersion, setAffiliatesVersion] = useState(0);
  const figures = useLoad(
    () => api.operatorAnalytics(operatorId, undefined, brand.brand_id),
    [operatorId, brand.brand_id],
  );
  const affiliates = useLoad(() => api.operatorAffiliates(operatorId), [operatorId, affiliatesVersion]);
  const enrolled = affiliates.state === 'ready'
    ? affiliatesOnBrand(affiliates.value.items, brand.brand_id)
    : [];

  return (
    <>
      <Panel title={t('brand.detail.figures')}>
        {figures.state === 'error' ? <Failure error={figures.error} /> : null}
        {figures.state === 'loading' ? <Loading rows={1} /> : null}
        {figures.state === 'ready' ? (
          <>
            {figures.value.unavailable_because ? (
              <Callout tone="caution">{figures.value.unavailable_because}</Callout>
            ) : null}
            {figures.value.is_provisional ? (
              <Callout tone="caution">
                {figures.value.provisional_because ?? t('brand.detail.provisional')}
              </Callout>
            ) : null}
            <OperatorFigures data={figures.value.data} />
            <Callout>{t('brand.detail.asOf', { date: figures.value.data_as_of })}</Callout>
          </>
        ) : null}
      </Panel>

      <Panel title={t('brand.detail.affiliatesTitle')}>
        {affiliates.state === 'error' ? <Failure error={affiliates.error} /> : null}
        {affiliates.state === 'loading' ? <Loading rows={1} /> : null}
        {affiliates.state === 'ready' ? (
          enrolled.length === 0 ? (
            <Empty>{t('brand.detail.nobody')}</Empty>
          ) : (
            <>
              {/*
                Name, state and joining date only. The same rows carry clicks,
                players and commission, and those figures are that affiliate's
                whole relationship with this operator rather than their work on
                this brand — printing them under a brand heading would state
                something untrue about every affiliate who works on two.
              */}
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <th>{t('brand.detail.colName')}</th><th>{t('brand.detail.colEmail')}</th><th>{t('brand.detail.colCountry')}</th><th>{t('brand.detail.colStatus')}</th><th className="num">{t('brand.detail.colJoined')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrolled.map((a) => {
                      const openThis = () => setOpenAffiliate(a.id);
                      return (
                      <tr key={a.id} className="row-clickable" onClick={openThis} {...rowOpen(openThis)}>
                        <td>{a.legal_name}</td>
                        <td className="faint">{a.email}</td>
                        <td>{countryLabel(a.country)}</td>
                        <td>
                          {/* Batch 2 review, finding 5 — same misuse as the
                              platform Affiliates panel: "Under Review" and
                              "Ended" both read as `provisional` (money still
                              moving), which neither is. Ties to the real
                              ledger status, same as there. */}
                          <Chip tone={affiliateStatusTone(a.status)}>
                            {a.shown_to_affiliate}
                          </Chip>
                          {a.is_dormant ? <Chip tone="neutral">{t('brand.detail.dormant')}</Chip> : null}
                        </td>
                        <td className="num faint">{formatDate(a.joined)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableScroll>
              <Callout>
                {t('brand.detail.openRowNote')}
              </Callout>
              {openAffiliate ? (
                <AffiliateDetailModal
                  api={api}
                  affiliateId={openAffiliate}
                  lockedBrand={{ brand_id: brand.brand_id, name: brand.display_name }}
                  onChanged={() => setAffiliateChanged(true)}
                  onClose={() => {
                    setOpenAffiliate(null);
                    // Re-read only when something changed: every read of the
                    // list is a logged access (§3.9c), not a free refresh.
                    if (affiliateChanged) {
                      setAffiliateChanged(false);
                      setAffiliatesVersion((v) => v + 1);
                    }
                  }}
                />
              ) : null}
            </>
          )
        ) : null}
      </Panel>

      {mayPropose ? (
        <Panel title={t('edit.brand.profileTitle')}>
          {/* Batch 2 review, finding 7 — the result of the proposal just
              made; read whole. */}
          {proposed ? <Callout fold={false}>{proposed}</Callout> : null}
          <ProposeBrandChange
            api={api}
            operatorId={operatorId}
            brand={brand}
            onDone={setProposed}
            onSaved={(p, note) => { setProposed(note); onProfile(p); }}
          />
        </Panel>
      ) : null}

      <Panel title={t('brand.detail.settingsTitle')}>
        <BrandSettings
          api={api}
          operatorId={operatorId}
          brandId={brand.brand_id}
          canWrite={can.edit}
        />
      </Panel>

      <Callout>
        {t('brand.detail.footnote')}
      </Callout>
    </>
  );
}
